import type { BankAccountRow, Facility, Pack } from '@/types/banking'
import { matchesAccountSearch } from '@/lib/account-search'
import {
  ACCOUNT_TYPE_LABELS,
  CONNECTION_COPY,
  EM_DASH,
  NO_PACK,
  UNASSIGNED_FACILITY,
  UNASSIGNED_FACILITY_LABEL,
  formatAccountNumber,
  isCashPositionLocked,
  toFilterEntries,
} from '@/components/ledgersync/connectionManagement.utils'

/**
 * Pure helpers for the manual-bank screens on Financial Accounts settings: the
 * list of manual banks and one manual bank's detail page.
 *
 * A MANUAL BANK HAS NO ROW OF ITS OWN. It is the group of manual bank accounts
 * (`integration_id IS NULL`, not deleted) sharing one `institution_name`, so
 * its identity is that name. Everything here works on `bankAccounts.list()`
 * rows and derives the banks from them. Copy lives in `manualBankCopy.ts`, the
 * bank form's save plan in `manualBankSave.ts`.
 */

// ---------------------------------------------------------------------------
// Account type
// ---------------------------------------------------------------------------

/** Every `account_type` enum value, including the ones the pickers do not offer. */
export type ManualAccountType = keyof typeof ACCOUNT_TYPE_LABELS

/** Narrow a raw `account_type` (the list helper types it `string | null`). */
export function isAccountType(value: unknown): value is ManualAccountType {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ACCOUNT_TYPE_LABELS, value)
}

/** Read-only label for the Type column; an unknown raw value is shown as-is. */
export function accountTypeLabel(raw: string | null): string {
  if (isAccountType(raw)) return ACCOUNT_TYPE_LABELS[raw]
  return raw ?? EM_DASH
}

// ---------------------------------------------------------------------------
// Banks
// ---------------------------------------------------------------------------

/** One manual bank, derived from its accounts. */
export interface ManualBank {
  /**
   * The exact stored name — what the data layer and the URL address the bank
   * by — or null for the unnamed group (NULL or empty `institution_name`).
   */
  institutionName: string | null
  /** Name to show; "Unknown institution" for the unnamed group. */
  displayName: string
  accounts: BankAccountRow[]
}

/** Locale-aware, case-insensitive text comparison for client-side sorting. */
export function compareText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true })
}

/**
 * Group the MANUAL accounts of a `bankAccounts.list()` result into banks.
 *
 * Grouped by the exact stored name, because rename addresses the group with an
 * exact `institution_name` match — merging " Chase" into "Chase" here would
 * show one bank whose edit then reaches only part of it.
 *
 * @param accounts - Bank account rows, linked ones included (they are dropped)
 * @returns Banks ordered by display name
 */
export function groupManualBanks(accounts: readonly BankAccountRow[]): ManualBank[] {
  // Keyed by the name, with null for the unnamed group, so no real name can
  // land in that group.
  const banks = new Map<string | null, ManualBank>()
  for (const account of accounts) {
    if (account.integration_id) continue
    const name = account.institution_name === '' ? null : account.institution_name
    let bank = banks.get(name)
    if (!bank) {
      bank = {
        institutionName: name,
        displayName: name ?? CONNECTION_COPY.unknownInstitution,
        accounts: [],
      }
      banks.set(name, bank)
    }
    bank.accounts.push(account)
  }
  return [...banks.values()].sort((a, b) => compareText(a.displayName, b.displayName))
}

/**
 * Detail-page path of a manual bank below a Financial Accounts page
 * (`financialAccountsBasePath(scope)`); the name is URL-encoded.
 *
 * The accounts without an institution name live at `manual-unnamed`, a path of
 * their own rather than a reserved name under `manual/`: any reserved name
 * could also be a real bank's name.
 *
 * @param basePath - The Financial Accounts page the detail page hangs below
 * @param bank - The bank, addressed by its exact stored name
 */
export function manualBankPath(basePath: string, bank: Pick<ManualBank, 'institutionName'>): string {
  return bank.institutionName === null
    ? `${basePath}/manual-unnamed`
    : `${basePath}/manual/${encodeURIComponent(bank.institutionName)}`
}

/** A manual bank as one Financial Accounts page lists it. */
export interface ManualBankInScope extends ManualBank {
  /**
   * Every live account of the bank — any facility, inactive ones included —
   * which the bank-wide Edit and Delete act on; `accounts` is only what the
   * page lists. See {@link manualBanksInScope}.
   */
  bankAccountIds: string[]
}

/**
 * The manual banks a Financial Accounts page lists.
 *
 * Organization-wide (`facilityId` null) a bank lists the accounts Settings
 * shows: active facilities and unassigned (ADR 0016). On a facility's page it
 * lists only that facility's accounts — even when the facility is inactive
 * (its page shows them read-only) — and only banks with at least one are listed.
 *
 * `bankAccountIds` is wider than what is listed: every account of the bank,
 * at any facility. Acting on fewer would split the bank — a rename would leave
 * the skipped accounts behind as a bank under the old name, which still blocks
 * that name and reappears when their facility is reactivated; a delete would
 * leave them as such a bank.
 *
 * @param accounts - Every manual and linked bank account of the organization, inactive facilities included
 * @param facilities - Every facility, inactive included
 * @param facilityId - The facility the page is scoped to; null organization-wide
 */
export function manualBanksInScope(
  accounts: readonly BankAccountRow[],
  facilities: readonly Facility[],
  facilityId: string | null,
): ManualBankInScope[] {
  return reachableManualBanks(accounts, facilities, facilityId).filter((bank) => bank.accounts.length > 0)
}

/**
 * One manual bank as a page sees it, by its exact stored name — unlike
 * {@link manualBanksInScope}, also when the page lists none of its accounts:
 * then `accounts` is empty while `bankAccountIds` still lists the bank's
 * accounts elsewhere, which the bank-wide Edit and Delete act on. Null when
 * the bank has no live account at all.
 *
 * @param accounts - As for {@link manualBanksInScope}
 * @param facilities - Every facility, inactive included
 * @param facilityId - The facility the page is scoped to; null organization-wide
 * @param institutionName - The bank's exact stored name, null for the unnamed group
 */
export function findManualBankInScope(
  accounts: readonly BankAccountRow[],
  facilities: readonly Facility[],
  facilityId: string | null,
  institutionName: string | null,
): ManualBankInScope | null {
  return (
    reachableManualBanks(accounts, facilities, facilityId).find(
      (bank) => bank.institutionName === institutionName,
    ) ?? null
  )
}

/** Every manual bank, with `accounts` narrowed to what the page lists. */
function reachableManualBanks(
  accounts: readonly BankAccountRow[],
  facilities: readonly Facility[],
  facilityId: string | null,
): ManualBankInScope[] {
  const isListed = (account: BankAccountRow) =>
    facilityId === null ? isShownInSettings(account, facilities) : account.facility_id === facilityId
  return groupManualBanks(accounts).map((bank) => ({
    ...bank,
    accounts: bank.accounts.filter(isListed),
    bankAccountIds: bank.accounts.map((a) => a.id),
  }))
}

/**
 * Whether `candidate` would collide with another manual bank's name.
 *
 * Other banks are compared case-insensitively and trimmed on both sides,
 * because stored names predate the form's trimming. The bank being renamed is
 * excluded by its EXACT stored name only: "Chase " is a different bank from
 * "Chase", and saving one must not quietly merge it into the other.
 *
 * @param accounts - Every bank account of the organization (not only active facilities)
 * @param candidate - The name the user typed
 * @param currentName - The bank's exact stored name when renaming, null when creating
 */
export function isDuplicateInstitutionName(
  accounts: readonly BankAccountRow[],
  candidate: string,
  currentName: string | null,
): boolean {
  const target = candidate.trim().toLowerCase()
  return accounts.some(
    (account) =>
      !account.integration_id &&
      account.institution_name !== null &&
      account.institution_name !== currentName &&
      account.institution_name.trim().toLowerCase() === target,
  )
}

// ---------------------------------------------------------------------------
// Facilities
// ---------------------------------------------------------------------------

/**
 * Whether Settings shows an account: no facility, or an active one — the
 * client-side twin of `bankAccounts.list({ activeOnly: true })` (ADR 0016).
 *
 * @param account - The account
 * @param facilities - Every facility, inactive included
 */
export function isShownInSettings(
  account: Pick<BankAccountRow, 'facility_id'>,
  facilities: readonly Facility[],
): boolean {
  if (!account.facility_id) return true
  return facilities.some((f) => f.id === account.facility_id && f.is_active)
}

/** One option of a facility picker. */
export interface FacilityOption {
  id: string
  name: string
  code: string
  inactive: boolean
}

/**
 * Facility picker options for one account: the active facilities, plus the
 * account's own facility when it has since been deactivated — dropping it
 * would make saving an unrelated edit silently unassign the account.
 *
 * @param facilities - Every facility of the organization, inactive included
 * @param currentFacilityId - The facility the account holds now, if any
 */
export function facilityOptionsFor(
  facilities: readonly Facility[],
  currentFacilityId: string | null,
): FacilityOption[] {
  const options = facilities
    .filter((f) => f.is_active)
    .map((f) => ({ id: f.id, name: f.name, code: f.code, inactive: false }))
  const current = currentFacilityId ? facilities.find((f) => f.id === currentFacilityId) : undefined
  if (current && !current.is_active) {
    options.push({ id: current.id, name: current.name, code: current.code, inactive: true })
  }
  return options
}

// ---------------------------------------------------------------------------
// Accounts table
// ---------------------------------------------------------------------------

/** An account with the display values the detail table shows, sorts and filters on. */
export interface ManualAccountTableRow {
  account: BankAccountRow
  numberLabel: string
  typeLabel: string
  facilityName: string
  /** False when the row reads "Unassigned" — shown muted. */
  facilityAssigned: boolean
  packId: string | null
  packName: string | null
  /** A credit card: never in Cash Position, its toggle disabled. */
  cashPositionLocked: boolean
  /**
   * Counts towards Cash Position — always false for a credit card. The sort
   * key of the column; the toggle gets the stored flag and applies the same rule.
   */
  inCashPosition: boolean
}

/**
 * Resolve each account's facility and pack names.
 *
 * @param accounts - The bank's accounts
 * @param facilities - Every facility, so a name always resolves
 * @param packs - The organization's packs
 */
export function toManualAccountTableRows(
  accounts: readonly BankAccountRow[],
  facilities: readonly Facility[],
  packs: readonly Pack[],
): ManualAccountTableRow[] {
  const facilityById = new Map(facilities.map((f) => [f.id, f]))
  const packById = new Map(packs.map((p) => [p.id, p]))
  return accounts.map((account) => {
    const facility = account.facility_id ? facilityById.get(account.facility_id) : undefined
    const packId = facility?.pack_id ?? null
    const cashPositionLocked = isCashPositionLocked(account)
    return {
      account,
      numberLabel: formatAccountNumber(account.account_number),
      typeLabel: accountTypeLabel(account.account_type),
      // A facility id that does not resolve is shown as Unassigned rather than
      // as a raw uuid.
      facilityName: facility?.name ?? UNASSIGNED_FACILITY_LABEL,
      facilityAssigned: facility !== undefined,
      packId,
      packName: packId ? (packById.get(packId)?.name ?? null) : null,
      cashPositionLocked,
      inCashPosition: account.include_in_cash_position && !cashPositionLocked,
    }
  })
}

/** The detail table's client-side filters. */
export interface ManualAccountFilters {
  search: string
  /** Raw Pack-filter values, possibly containing `NO_PACK`. */
  packs: string[]
  /** Raw Facility-filter values, possibly containing `UNASSIGNED_FACILITY`. */
  facilities: string[]
}

/** Whether any filter narrows the table (drives the filtered-empty copy). */
export function hasActiveFilters(filters: ManualAccountFilters): boolean {
  return filters.search.trim() !== '' || filters.packs.length > 0 || filters.facilities.length > 0
}

/**
 * Apply search + Pack + Facility filters with the connections table's
 * semantics: search matches name or account number (`matchesAccountSearch`); "No
 * Pack" and "Unassigned" select the accounts without one; filters combine
 * with AND, and an empty selection does not filter.
 */
export function filterManualAccountRows(
  rows: readonly ManualAccountTableRow[],
  filters: ManualAccountFilters,
): ManualAccountTableRow[] {
  const packs = toFilterEntries(filters.packs, NO_PACK)
  const facilities = toFilterEntries(filters.facilities, UNASSIGNED_FACILITY)
  return rows.filter((row) => {
    if (!matchesAccountSearch(row.account, filters.search)) return false
    if (packs && !packs.includes(row.packId)) return false
    if (facilities && !facilities.includes(row.account.facility_id)) return false
    return true
  })
}
